// Calendar view header card: range title + day count, training- and
// nutrition-phase bands, and the Target plans timeline. Grouped into one
// component since all sit above the week grid, share the same fromUtc/toUtc
// range, and are styled as a single card (design reference: dark card,
// calendar icon + month title, labeled rows with a divider between phase
// bands and plan timeline). Each PhaseRow returns null when its own track
// has no spans, so an athlete with only one track shows just that row.

import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../../parts/SvgIcon";
import { PhaseRow, PlanTimelineRow } from "./CalendarTimelines";
import { fmtUtc, translateCalendarHeader } from "./viewTokens";
import { Dot } from "./viewAtoms";

const CALENDAR_ICON = "/img/new-icons/calendar-ico-white.svg";

// Formats the range title
const formatRangeTitle = (fromUtc, toUtc) => {
  const start = moment(String(fromUtc), "YYYYMMDD");
  const end = moment(String(toUtc), "YYYYMMDD");
  if (start.year() === end.year() && start.month() === end.month()) {
    return start.format("MMMM YYYY");
  }
  return `${fmtUtc(fromUtc)} – ${fmtUtc(toUtc)}`;
};

export const CalendarHeader = ({
  fromUtc,
  toUtc,
  totalDays,
  loggedDays,
  phaseSpans,
  nutritionPhaseSpans = [],
  visiblePlans,
}) => {
  const { t } = useTranslation("athlete-portal");

  const hasAnyPhase = phaseSpans.length > 0 || nutritionPhaseSpans.length > 0;

  return (
    <div className="np-cal-header">
      <div className="np-cal-header__title-row">
        <SvgIcon src={CALENDAR_ICON} useCurrentColor classNames="np-cal-header__icon" />
        <span className="np-cal-header__title">{formatRangeTitle(fromUtc, toUtc)}</span>
        <span className="np-cal-header__count">
          {translateCalendarHeader(t, "dayCountTotal", { totalDays })}
          <Dot />
          {translateCalendarHeader(t, "dayCountLogged", { loggedDays })}
        </span>
      </div>

      <PhaseRow
        label={translateCalendarHeader(t, "training")}
        spans={phaseSpans}
        fromUtc={fromUtc}
        toUtc={toUtc}
      />

      <PhaseRow
        label={translateCalendarHeader(t, "nutrition")}
        spans={nutritionPhaseSpans}
        fromUtc={fromUtc}
        toUtc={toUtc}
      />

      {hasAnyPhase && visiblePlans.length > 0 && (
        <div className="np-cal-header__divider" />
      )}

      <PlanTimelineRow
        label={translateCalendarHeader(t, "targetPlans")}
        plans={visiblePlans}
        fromUtc={fromUtc}
        toUtc={toUtc}
      />
    </div>
  );
};

CalendarHeader.propTypes = {
  fromUtc: PropTypes.number.isRequired,
  toUtc: PropTypes.number.isRequired,
  totalDays: PropTypes.number.isRequired,
  loggedDays: PropTypes.number.isRequired,
  phaseSpans: PropTypes.array.isRequired,
  nutritionPhaseSpans: PropTypes.array,
  visiblePlans: PropTypes.array.isRequired,
};

export default CalendarHeader;
